import React, { useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/hooks/use-toast';

interface AddBrokerModalProps {
  isOpen: boolean;
  onClose: () => void;
  brokerageId: string;
}

export const AddBrokerModal = ({ isOpen, onClose, brokerageId }: AddBrokerModalProps) => {
  const [fullName, setFullName] = useState('');
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');
  const queryClient = useQueryClient();
  const { toast } = useToast();

  const addBrokerMutation = useMutation({
    mutationFn: async () => {
      const { error } = await supabase
        .from('brokers')
        .insert({
          full_name: fullName,
          email: email || null,
          phone: phone || null,
          brokerage_id: brokerageId
        });

      if (error) {
        console.error('Error adding broker:', error);
        throw error;
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['brokers'] });
      toast({
        title: 'Success',
        description: 'Broker added successfully',
      });
      onClose();
      resetForm();
    },
    onError: (error) => {
      console.error('Failed to add broker:', error);
      toast({
        title: 'Error',
        description: 'Failed to add broker. Please try again.',
        variant: 'destructive',
      });
    }
  });

  const resetForm = () => {
    setFullName('');
    setEmail('');
    setPhone('');
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (fullName.trim()) {
      addBrokerMutation.mutate();
    }
  };

  const handleClose = () => {
    onClose();
    resetForm();
  };

  return (
    <Dialog open={isOpen} onOpenChange={handleClose}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>Add New Broker</DialogTitle>
        </DialogHeader>
        
        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <Label htmlFor="fullName">Full Name *</Label>
            <Input
              id="fullName"
              value={fullName}
              onChange={(e) => setFullName(e.target.value)}
              placeholder="Enter broker's full name"
              required
            />
          </div>
          
          <div>
            <Label htmlFor="email">Email</Label>
            <Input
              id="email"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="Enter broker's email"
            />
          </div>
          
          <div>
            <Label htmlFor="phone">Phone Number</Label>
            <Input
              id="phone"
              value={phone}
              onChange={(e) => setPhone(e.target.value)}
              placeholder="Enter broker's phone number"
            />
          </div>
          
          <div className="flex justify-end space-x-2 pt-4">
            <Button
              type="button"
              variant="outline"
              onClick={handleClose}
              disabled={addBrokerMutation.isPending}
            >
              Cancel
            </Button>
            <Button
              type="submit"
              disabled={!fullName.trim() || addBrokerMutation.isPending}
            >
              {addBrokerMutation.isPending ? 'Adding...' : 'Add Broker'}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
};